/**
 * SearchProviderSelector 组件
 *
 * 搜索供应商选择器，左侧栏组件。
 * 支持供应商列表选择、添加新供应商、删除供应商。
 */

import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared';

export interface SearchProviderOption {
  provider_name: string;
  display_name: string;
  provider_type: string;
  enabled: boolean;
  priority: number;
  weight: number;
  search_types: string[];
  key_count: number;
  enabled_key_count: number;
}

export interface SearchProviderSelectorProps {
  providers: SearchProviderOption[];
  selectedProvider: string | null;
  onSelect: (providerName: string) => void;
  onAdd: () => void;
  onDelete: (providerName: string) => void;
  disabled?: boolean;
  className?: string;
}

const PROVIDER_COLORS: Record<string, string> = {
  bailian: 'border-orange-200 bg-orange-50',
  tavily: 'border-blue-200 bg-blue-50',
  qianfan: 'border-cyan-200 bg-cyan-50',
  shuqi: 'border-amber-200 bg-amber-50',
};

const getProviderColor = (providerType: string): string => {
  return PROVIDER_COLORS[providerType] || 'border-gray-200 bg-gray-50';
};

const SEARCH_TYPE_LABELS: Record<string, string> = {
  general: '一般',
  novel: '小说',
};

export const SearchProviderSelector: React.FC<SearchProviderSelectorProps> = ({
  providers,
  selectedProvider,
  onSelect,
  onAdd,
  onDelete,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const handleSelect = useCallback((providerName: string) => {
    onSelect(providerName);
    setIsOpen(false);
  }, [onSelect]);

  const handleDelete = useCallback((e: React.MouseEvent, providerName: string) => {
    e.stopPropagation();
    if (showDeleteConfirm === providerName) {
      onDelete(providerName);
      setShowDeleteConfirm(null);
    } else {
      setShowDeleteConfirm(providerName);
      setTimeout(() => setShowDeleteConfirm(null), 3000);
    }
  }, [onDelete, showDeleteConfirm]);

  const selectedOption = providers.find(p => p.provider_name === selectedProvider);

  return (
    <div className={clsx('relative', className)}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        选择供应商
      </label>

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-all',
          selectedOption ? getProviderColor(selectedOption.provider_type) : 'bg-white border-gray-300',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer',
          isOpen && 'ring-2 ring-blue-500 border-blue-500'
        )}
      >
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-3 h-3 rounded-full',
            selectedOption?.enabled ? 'bg-green-400' : 'bg-gray-300'
          )} />
          <div>
            <div className="font-medium text-gray-900">
              {selectedOption?.display_name || selectedOption?.provider_name || '请选择供应商'}
            </div>
            {selectedOption && (
              <div className="text-xs text-gray-500 mt-0.5">
                {selectedOption.enabled_key_count}/{selectedOption.key_count} 密钥 · 优先级 {selectedOption.priority}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedOption && (
            <StatusBadge 
              variant={selectedOption.enabled ? 'success' : 'neutral'} 
              size="sm"
            >
              {selectedOption.enabled ? '启用' : '禁用'}
            </StatusBadge>
          )}
          <svg
            className={clsx('w-5 h-5 text-gray-400 transition-transform', isOpen && 'rotate-180')}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
            <button
              type="button"
              onClick={() => { onAdd(); setIsOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-b border-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              添加新供应商
            </button>

            {providers.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                暂无供应商配置
              </div>
            ) : (
              <div className="py-1">
                {providers.map((provider) => (
                  <div
                    key={provider.provider_name}
                    onClick={() => handleSelect(provider.provider_name)}
                    className={clsx(
                      'group flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                      selectedProvider === provider.provider_name ? 'bg-blue-50' : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        'w-3 h-3 rounded-full',
                        provider.enabled ? 'bg-green-400' : 'bg-gray-300'
                      )} />
                      <div>
                        <div className={clsx(
                          'font-medium',
                          selectedProvider === provider.provider_name ? 'text-blue-700' : 'text-gray-900'
                        )}>
                          {provider.display_name || provider.provider_name}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          <span>{provider.enabled_key_count}/{provider.key_count} 密钥</span>
                          <span>·</span>
                          <span>优先级 {provider.priority}</span>
                          {provider.search_types.length > 0 && (
                            <>
                              <span>·</span>
                              <div className="flex gap-1">
                                {provider.search_types.map(t => (
                                  <span key={t} className="px-1 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">
                                    {SEARCH_TYPE_LABELS[t] || t}
                                  </span>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge 
                        variant={provider.enabled ? 'success' : 'neutral'} 
                        size="sm"
                      >
                        {provider.enabled ? '启用' : '禁用'}
                      </StatusBadge>
                      <button
                        type="button"
                        onClick={(e) => handleDelete(e, provider.provider_name)}
                        className={clsx(
                          'p-1 rounded transition-colors',
                          showDeleteConfirm === provider.provider_name 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100'
                        )}
                        title={showDeleteConfirm === provider.provider_name ? '确认删除?' : '删除供应商'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchProviderSelector;